import React, { useState, useEffect, useRef } from 'react'

// Recorte circular de uma fotografia antes do upload: arrastar para
// posicionar, cursor para o zoom. Sem dependências externas — usa apenas
// canvas e pointer events (mouse + touque).

const CAIXA = 260  // px, área quadrada de recorte visível
const SAIDA = 480  // px, resolução do ficheiro final exportado

function escalaCobertura(img) {
  return Math.max(CAIXA / img.width, CAIXA / img.height)
}

function limitar(offset, largDisp, altDisp) {
  return {
    x: Math.min(0, Math.max(CAIXA - largDisp, offset.x)),
    y: Math.min(0, Math.max(CAIXA - altDisp, offset.y)),
  }
}

export default function CropadorFoto({ file, onConfirmar, onCancelar }) {
  const [img, setImg] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [aGuardar, setAGuardar] = useState(false)
  const arrastoRef = useRef(null)

  useEffect(() => {
    const url = URL.createObjectURL(file)
    const imagem = new Image()
    imagem.onload = () => {
      const base = escalaCobertura(imagem)
      setImg(imagem)
      setZoom(1)
      setOffset({
        x: (CAIXA - imagem.width * base) / 2,
        y: (CAIXA - imagem.height * base) / 2,
      })
    }
    imagem.src = url
    return () => URL.revokeObjectURL(url)
  }, [file])

  const escalaTotal = img ? escalaCobertura(img) * zoom : 1
  const largDisp = img ? img.width * escalaTotal : 0
  const altDisp = img ? img.height * escalaTotal : 0

  function mudarZoom(novoZoom) {
    if (!img) return
    const base = escalaCobertura(img)
    const escalaAnterior = base * zoom
    const escalaNova = base * novoZoom
    const centro = CAIXA / 2
    // mantém o mesmo ponto da imagem no centro da caixa ao mudar o zoom
    const pontoX = (centro - offset.x) / escalaAnterior
    const pontoY = (centro - offset.y) / escalaAnterior
    setZoom(novoZoom)
    setOffset(limitar(
      { x: centro - pontoX * escalaNova, y: centro - pontoY * escalaNova },
      img.width * escalaNova, img.height * escalaNova
    ))
  }

  function iniciarArrasto(e) {
    if (!img) return
    arrastoRef.current = { x: e.clientX, y: e.clientY, offsetX: offset.x, offsetY: offset.y }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function moverArrasto(e) {
    if (!arrastoRef.current) return
    const dx = e.clientX - arrastoRef.current.x
    const dy = e.clientY - arrastoRef.current.y
    setOffset(limitar(
      { x: arrastoRef.current.offsetX + dx, y: arrastoRef.current.offsetY + dy },
      largDisp, altDisp
    ))
  }

  function terminarArrasto() {
    arrastoRef.current = null
  }

  function confirmar() {
    if (!img) return
    setAGuardar(true)
    const canvas = document.createElement('canvas')
    canvas.width = SAIDA
    canvas.height = SAIDA
    const ctx = canvas.getContext('2d')
    const sx = (0 - offset.x) / escalaTotal
    const sy = (0 - offset.y) / escalaTotal
    const sSize = CAIXA / escalaTotal
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, SAIDA, SAIDA)
    canvas.toBlob(blob => {
      setAGuardar(false)
      if (blob) onConfirmar(blob)
    }, 'image/jpeg', 0.9)
  }

  return (
    <div onClick={onCancelar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '20px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 'var(--raio-lg)', width: '100%', maxWidth: '340px', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
        <p style={{ fontWeight: '600', fontSize: '15px', alignSelf: 'flex-start', margin: 0 }}>Ajustar fotografia</p>

        <div
          onPointerDown={iniciarArrasto}
          onPointerMove={moverArrasto}
          onPointerUp={terminarArrasto}
          onPointerCancel={terminarArrasto}
          style={{
            width: CAIXA, height: CAIXA, borderRadius: '50%',
            overflow: 'hidden', position: 'relative', touchAction: 'none',
            cursor: img ? 'grab' : 'default', background: '#EEE',
            border: '1px solid var(--cor-borda)', flexShrink: 0,
          }}
        >
          {img ? (
            <img
              src={img.src}
              alt=""
              draggable={false}
              style={{
                position: 'absolute', left: 0, top: 0,
                width: largDisp, height: altDisp, maxWidth: 'none',
                transform: `translate(${offset.x}px, ${offset.y}px)`,
                userSelect: 'none', pointerEvents: 'none',
              }}
            />
          ) : (
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '12px', color: 'var(--cor-texto-suave)', margin: 0 }}>
              A carregar…
            </p>
          )}
        </div>

        <input
          type="range" min="1" max="3" step="0.01"
          value={zoom}
          onChange={e => mudarZoom(parseFloat(e.target.value))}
          disabled={!img}
          style={{ width: '100%', accentColor: 'var(--cor-principal)' }}
        />
        <p style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', textAlign: 'center', margin: 0 }}>
          Arraste a imagem para a posicionar e use o cursor para o zoom.
        </p>

        <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
          <button type="button" onClick={onCancelar} className="btn-secundario" style={{ flex: 1, padding: '9px 0', fontSize: '14px' }}>
            Cancelar
          </button>
          <button
            type="button" onClick={confirmar} className="btn-primario"
            disabled={!img || aGuardar}
            style={{ flex: 1, padding: '9px 0', fontSize: '14px', opacity: (!img || aGuardar) ? 0.6 : 1 }}
          >
            {aGuardar ? 'A guardar…' : 'Usar foto'}
          </button>
        </div>
      </div>
    </div>
  )
}
